'use client';

import { ReactNode, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, AriaAttributes } from 'react';

import classNames from 'classnames';
import { FocusTrap } from 'focus-trap-react';
import { CSSTransition } from 'react-transition-group';

import { useBodyOverflowHidden } from '../../../../hooks/useBodyOverflowHidden/useBodyOverflowHidden';
import { useBreakpoint } from '../../../../hooks/useBreakpoint/useBreakpoint';
import { Backdrop } from '../../../backdrop/components/Backdrop/Backdrop';
import { ClientPortal } from '../../../client-portal/components/ClientPortal/ClientPortal';

import styles from './Flyout.module.css';
import { FlyoutActions } from './FlyoutActions';
import { FlyoutContent } from './FlyoutContent';
import { FlyoutHeader } from './FlyoutHeader';
import { FlyoutContext } from './context';

interface Props {
   open?: boolean;
   position?: 'left' | 'right' | 'bottom';
   closeOnOutsideClick?: boolean;
   width?: string;
   ariaLabel?: AriaAttributes['aria-label'];
   ariaLabelledBy?: AriaAttributes['aria-labelledby'];
   onFlyoutClose?: () => void;
   children: ReactNode | Array<ReactNode>;
}

const Flyout = ({ open = false, position: positionProp, closeOnOutsideClick = true, width, ariaLabel, ariaLabelledBy, onFlyoutClose, children }: Props) => {
   const titleId = useId();

   const nodeRef = useRef(null);

   const { desktop } = useBreakpoint();

   const [flyoutOpen, setFlyoutOpen] = useState(open);
   const [focusTrapActive, setFocusTrapActive] = useState(false);
   const [trigger, setTrigger] = useState<HTMLElement | null>(null);

   const position = positionProp ?? (desktop ? 'right' : 'bottom');
   const transitionClassNames = {
      enter: styles[`flyout-${position}-enter`],
      enterActive: styles[`flyout-${position}-enter-active`],
      exit: styles[`flyout-${position}-exit`],
      exitActive: styles[`flyout-${position}-exit-active`]
   };

   useBodyOverflowHidden(open);

   const providerValue = useMemo(
      () => ({
         titleId,
         closeOnOutsideClick,
         isOpen: open,
         position,
         onClose: onFlyoutClose
      }),
      [titleId, closeOnOutsideClick, position, open, onFlyoutClose]
   );

   useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
         if (event.key === 'Escape') {
            onFlyoutClose?.();
         }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
         document.removeEventListener('keydown', handleKeyDown);
      };
   }, [onFlyoutClose]);

   useLayoutEffect(() => {
      if (open) {
         setTrigger(document.activeElement as HTMLElement | null);
         setFlyoutOpen(true);
      } else {
         setFlyoutOpen(false);
      }
   }, [open]);

   return (
      <ClientPortal selector="body">
         <FlyoutContext value={providerValue}>
            <Backdrop type="flyout" isOpen={open} closeOnOutsideClick={closeOnOutsideClick} onClose={onFlyoutClose} />

            <CSSTransition
               nodeRef={nodeRef}
               in={flyoutOpen}
               timeout={200}
               classNames={transitionClassNames}
               mountOnEnter
               unmountOnExit
               onEnter={(): void => {
                  setFocusTrapActive(true);
               }}
               onExited={(): void => {
                  setFocusTrapActive(false);
                  setTrigger(null);
               }}
            >
               <FocusTrap
                  active={focusTrapActive}
                  focusTrapOptions={{
                     allowOutsideClick: true,
                     tabbableOptions: { displayCheck: 'none' },
                     setReturnFocus: () => trigger ?? /* istanbul ignore next */ false
                  }}
               >
                  <div
                     ref={nodeRef}
                     role="dialog"
                     aria-label={ariaLabel}
                     aria-labelledby={ariaLabel ? undefined : ariaLabelledBy ?? titleId}
                     aria-modal="true"
                     style={{ width: width ?? (!desktop ? '100%' : 'var(--component-surfaces-flyout-container-sizing-max-width)') }}
                     className={classNames(
                        'bg-component-surfaces-flyout-container-color-background border-component-color-flyout-border fixed flex flex-col z-[9999] shadow-component-surfaces-flyout-container-box-shadow',
                        position === 'right' && 'right-0 rounded-l-component-surfaces-flyout-container-border-radius',
                        position === 'left' && 'left-0 rounded-r-component-surfaces-flyout-container-border-radius',
                        (position === 'left' || position === 'right') && 'min-w-component-surfaces-flyout-container-sizing-min-width max-w-component-surfaces-flyout-container-sizing-max-width inset-y-0',
                        position === 'bottom' && 'bottom-0 rounded-t-component-surfaces-flyout-container-border-radius phone:h-screen tab-port:h-screen h-component-surfaces-flyout-container-sizing-max-height inset-x-0'
                     )}
                  >
                     {children}
                  </div>
               </FocusTrap>
            </CSSTransition>
         </FlyoutContext>
      </ClientPortal>
   );
};

export { Flyout, FlyoutHeader, FlyoutContent, FlyoutActions };
